/* eslint-disable no-nested-ternary */
/* eslint-disable indent */
/* eslint-disable max-len */
import './style.scss';

import { useMemo } from 'react';

import { useTranslation } from 'react-i18next';
import { useMutation, useQuery } from 'react-query';
import { useParams, useSearchParams } from 'react-router-dom';

import {
  downloadFile,
  getAllProposedExpenditureTypes,
  getDocumentsByVersion,
  getExpenditureByVersion,
  getGeneralInfoByVersion,
  getResearchResultByVersion,
} from 'apis/projectProposals.api';

import ErrorDialog from 'components/ErrorDialog';
import Loading from 'components/Loading';

import {
  defaultExpenditureValues,
  defaultGeneralInfoValues,
  defaultResearchResultValues,
} from 'features/ReviseProjectProposal/defaultValues';

import { ORGANIZATION_TYPES, PROJECT_ROLES, TIMEZONE_VN, currencyOptions } from 'constant';
import {
  ContactCompareTable,
  DiffColumn,
  ExpenditureCompareTable,
  HardProductCompareTable,
  KPICompareTable,
  RecommendedExpertCompareTable,
  SoftProductCompareTable,
} from 'features/ReviseProjectProposal/components/CompareDetail';
import { diff, formatFileName, getAcadamicLevelShortHand, highlight } from 'utils/func';
import { generateDiffStrings, renderDiffString } from 'utils/func/diffString';
import RichTextViewer from 'components/RichTextViewer';

export default function ReviseProjectProposalCompareVersion() {
  // #region Data
  const { t } = useTranslation();
  const { projectProposalId } = useParams();
  const [searchParams] = useSearchParams();
  const version = searchParams.get('version');
  const previousVersion = version > 1 ? version - 1 : false;
  const mapVersionName = {
    1: t('projectProposal.compareVersion.genericVersion'),
    2: t('projectProposal.compareVersion.preliminarySelectionCouncilVersion'),
    3: t('projectProposal.compareVersion.specializationCouncilVersion'),
    4: t('projectProposal.compareVersion.financeCouncilVersion'),
  };

  const {
    data: generalInfoData1,
    isLoading: isGeneralInfoLoading1,
    isError: isGeneralInfoError1,
    error: generalInfoError1,
  } = useQuery(['project-proposal-general-info', projectProposalId, previousVersion], () =>
    getGeneralInfoByVersion(projectProposalId, previousVersion)
  );
  const {
    data: generalInfoData2,
    isLoading: isGeneralInfoLoading2,
    isError: isGeneralInfoError2,
    error: generalInfoError2,
  } = useQuery(['project-proposal-general-info', projectProposalId, version], () =>
    getGeneralInfoByVersion(projectProposalId, version)
  );

  const generalInfo1 = useMemo(
    () => generalInfoData1?.data ?? defaultGeneralInfoValues,
    [generalInfoData1]
  );
  const generalInfo2 = useMemo(
    () => generalInfoData2?.data ?? defaultGeneralInfoValues,
    [generalInfoData2]
  );

  const {
    data: researchResultData1,
    isLoading: isResearchResultLoading1,
    isError: isResearchResultError1,
    error: researchResultError1,
  } = useQuery(['project-proposal-research-result', projectProposalId, previousVersion], () =>
    getResearchResultByVersion(projectProposalId, previousVersion)
  );

  const {
    data: researchResultData2,
    isLoading: isResearchResultLoading2,
    isError: isResearchResultError2,
    error: researchResultError2,
  } = useQuery(['project-proposal-research-result', projectProposalId, version], () =>
    getResearchResultByVersion(projectProposalId, version)
  );

  const researchResult1 = useMemo(
    () => researchResultData1?.data ?? defaultResearchResultValues,
    [researchResultData1]
  );
  const researchResult2 = useMemo(
    () => researchResultData2?.data ?? defaultResearchResultValues,
    [researchResultData2]
  );

  const { data: expenditureTypesData } = useQuery(
    ['projectProposal-expenditure-types'],
    getAllProposedExpenditureTypes
  );

  const {
    data: expenditureData1,
    isLoading: isExpenditureLoading1,
    isError: isExpenditureError1,
    error: expenditureError1,
  } = useQuery(['project-proposal-expenditure', projectProposalId, previousVersion], () =>
    getExpenditureByVersion(projectProposalId, previousVersion)
  );

  const {
    data: expenditureData2,
    isLoading: isExpenditureLoading2,
    isError: isExpenditureError2,
    error: expenditureError2,
  } = useQuery(['project-proposal-expenditure', projectProposalId, version], () =>
    getExpenditureByVersion(projectProposalId, version)
  );

  const expenditure1 = useMemo(() => {
    if (expenditureData1?.data && expenditureTypesData?.data) {
      return {
        ...expenditureData1.data,
        proposedExpenditureSummaries:
          expenditureData1.data?.proposedExpenditureSummaries?.length === 7
            ? expenditureData1.data.proposedExpenditureSummaries
            : expenditureTypesData.data?.map((_expenditure, index) => {
                const expenditureSummaries =
                  expenditureData1.data?.proposedExpenditureSummaries?.find(
                    (e) => e.proposedExpenditureTypeId === _expenditure.id
                  );
                let id = index;
                let expenditureFromVNUHCM = 0;
                let expenditureFromFunding = 0;
                if (expenditureSummaries) {
                  id = expenditureSummaries.id;
                  expenditureFromVNUHCM = expenditureSummaries?.expenditureFromVNUHCM;
                  expenditureFromFunding = expenditureSummaries?.expenditureFromFunding;
                }
                return {
                  id,
                  expenditureFromVNUHCM,
                  expenditureFromFunding,
                  proposedExpenditureTypeId: _expenditure.id,
                  proposedExpenditureType: _expenditure,
                };
              }),
      };
    }
    return defaultExpenditureValues;
  });
  const expenditure2 = useMemo(() => {
    if (expenditureData2?.data && expenditureTypesData?.data) {
      return {
        ...expenditureData2.data,
        proposedExpenditureSummaries:
          expenditureData2.data.proposedExpenditureSummaries?.length === 7
            ? expenditureData2.data?.proposedExpenditureSummaries
            : expenditureTypesData.data?.map((_expenditure, index) => {
                const expenditureSummaries =
                  expenditureData2.data?.proposedExpenditureSummaries?.find(
                    (e) => e.proposedExpenditureTypeId === _expenditure.id
                  );
                let id = index;
                let expenditureFromVNUHCM = 0;
                let expenditureFromFunding = 0;
                if (expenditureSummaries) {
                  id = expenditureSummaries.id;
                  expenditureFromVNUHCM = expenditureSummaries?.expenditureFromVNUHCM;
                  expenditureFromFunding = expenditureSummaries?.expenditureFromFunding;
                }
                return {
                  id,
                  expenditureFromVNUHCM,
                  expenditureFromFunding,
                  proposedExpenditureTypeId: _expenditure.id,
                  proposedExpenditureType: _expenditure,
                };
              }),
      };
    }
    return defaultExpenditureValues;
  });

  const {
    data: documentsData1,
    isLoading: isDocumentsLoading1,
    isError: isDocumentsError1,
    error: documentsError1,
  } = useQuery(['project-proposal-documents', projectProposalId, previousVersion], () =>
    getDocumentsByVersion(projectProposalId, previousVersion)
  );

  const {
    data: documentsData2,
    isLoading: isDocumentsLoading2,
    isError: isDocumentsError2,
    error: documentsError2,
  } = useQuery(['project-proposal-documents', projectProposalId, version], () =>
    getDocumentsByVersion(projectProposalId, version)
  );

  const documents1 = useMemo(() => documentsData1?.data ?? {}, [documentsData1]);
  const documents2 = useMemo(() => documentsData2?.data ?? {}, [documentsData2]);

  const expenditureInGeneral1 = useMemo(() => {
    const totalExpenditure =
      expenditure1?.proposedExpenditureSummaries?.reduce((sum, cur) => {
        if (cur?.proposedExpenditureType?.subProposedExpenditureTypeId === null) {
          const expenditureFromFunding = cur?.expenditureFromFunding ?? 0;
          const expenditureFromVNUHCM = cur?.expenditureFromVNUHCM ?? 0;

          return sum + expenditureFromFunding + expenditureFromVNUHCM;
        }
        return sum;
      }, 0) ?? 0;
    const nationalUniversityTotalExpenditure =
      expenditure1?.proposedExpenditureSummaries?.reduce((sum, cur) => {
        if (cur?.proposedExpenditureType?.subProposedExpenditureTypeId === null) {
          const expenditureFromVNUHCM = cur?.expenditureFromVNUHCM ?? 0;

          return sum + expenditureFromVNUHCM;
        }
        return sum;
      }, 0) ?? 0;
    let nationalUniversityNonallocatedExpenditure = 0;
    if (expenditure1?.proposedExpenditureSummaries?.length === 4) {
      nationalUniversityNonallocatedExpenditure =
        expenditure1?.proposedExpenditureSummaries?.[1]?.expenditureFromVNUHCM;
    } else if (expenditure1?.proposedExpenditureSummaries?.length === 7) {
      nationalUniversityNonallocatedExpenditure =
        expenditure1?.proposedExpenditureSummaries?.[4]?.expenditureFromVNUHCM;
    }
    const nationalUniversityAllocatedExpenditure =
      nationalUniversityTotalExpenditure - nationalUniversityNonallocatedExpenditure;

    const fundingExpenditure = totalExpenditure - nationalUniversityTotalExpenditure;
    const fundingExpenditureOther = generalInfo1?.fundingExpenditureOther ?? 0;
    const fundingExpenditureSelf = fundingExpenditure - fundingExpenditureOther;
    return {
      totalExpenditure,
      nationalUniversityTotalExpenditure,
      nationalUniversityNonallocatedExpenditure,
      nationalUniversityAllocatedExpenditure,
      fundingExpenditure,
      fundingExpenditureOther,
      fundingExpenditureSelf,
    };
  });
  const expenditureInGeneral2 = useMemo(() => {
    const totalExpenditure =
      expenditure2?.proposedExpenditureSummaries?.reduce((sum, cur) => {
        if (cur?.proposedExpenditureType?.subProposedExpenditureTypeId === null) {
          const expenditureFromFunding = cur?.expenditureFromFunding ?? 0;
          const expenditureFromVNUHCM = cur?.expenditureFromVNUHCM ?? 0;

          return sum + expenditureFromFunding + expenditureFromVNUHCM;
        }
        return sum;
      }, 0) ?? 0;
    const nationalUniversityTotalExpenditure =
      expenditure2?.proposedExpenditureSummaries?.reduce((sum, cur) => {
        if (cur?.proposedExpenditureType?.subProposedExpenditureTypeId === null) {
          const expenditureFromVNUHCM = cur?.expenditureFromVNUHCM ?? 0;

          return sum + expenditureFromVNUHCM;
        }
        return sum;
      }, 0) ?? 0;
    let nationalUniversityNonallocatedExpenditure = 0;
    if (expenditure2?.proposedExpenditureSummaries?.length === 4) {
      nationalUniversityNonallocatedExpenditure =
        expenditure2?.proposedExpenditureSummaries?.[1]?.expenditureFromVNUHCM;
    } else if (expenditure2?.proposedExpenditureSummaries?.length === 7) {
      nationalUniversityNonallocatedExpenditure =
        expenditure2?.proposedExpenditureSummaries?.[4]?.expenditureFromVNUHCM;
    }
    const nationalUniversityAllocatedExpenditure =
      nationalUniversityTotalExpenditure - nationalUniversityNonallocatedExpenditure;

    const fundingExpenditure = totalExpenditure - nationalUniversityTotalExpenditure;
    const fundingExpenditureOther = generalInfo2?.fundingExpenditureOther ?? 0;
    const fundingExpenditureSelf = fundingExpenditure - fundingExpenditureOther;
    return {
      totalExpenditure,
      nationalUniversityTotalExpenditure,
      nationalUniversityNonallocatedExpenditure,
      nationalUniversityAllocatedExpenditure,
      fundingExpenditure,
      fundingExpenditureOther,
      fundingExpenditureSelf,
    };
  });

  const generateResearchersWithDiffStrings = (data1 = [], data2 = []) => {
    const result1 = [...(data1 ?? [])];
    const result2 = [...(data2 ?? [])];
    for (let index = 0; index < Math.max(result1?.length ?? 0, result2?.length ?? 0); index += 1) {
      const diffStringsFullname = generateDiffStrings(
        `${
          !result1?.[index]?.academicRank && !result1?.[index]?.academicDegree
            ? ''
            : getAcadamicLevelShortHand(
                result1?.[index]?.academicRank,
                result1?.[index]?.academicDegree,
                t,
                true
              )
        }${result1?.[index]?.fullname ?? ''}`,
        `${
          !result2?.[index]?.academicRank && !result2?.[index]?.academicDegree
            ? ''
            : getAcadamicLevelShortHand(
                result2?.[index]?.academicRank,
                result2?.[index]?.academicDegree,
                t,
                true
              )
        }${result2?.[index]?.fullname ?? ''}`
      );
      const diffStringsOrganization = generateDiffStrings(
        result1?.[index]?.organization?.englishName === 'Other'
          ? result1?.[index]?.specificOrganizationName
          : t('lang') === 'vi'
          ? result1?.[index]?.organization?.name
          : result1?.[index]?.organization?.englishName,
        result2?.[index]?.organization?.englishName === 'Other'
          ? result2?.[index]?.specificOrganizationName
          : t('lang') === 'vi'
          ? result2?.[index]?.organization?.name
          : result2?.[index]?.organization?.englishName
      );
      if (result1?.[index]) {
        result1[index] = {
          ...result1?.[index],
          diffStringFullname: diffStringsFullname?.left,
          diffStringOrganization: diffStringsOrganization?.left,
        };
      }
      if (result2?.[index]) {
        result2[index] = {
          ...result2?.[index],
          diffStringFullname: diffStringsFullname?.right,
          diffStringOrganization: diffStringsOrganization?.right,
        };
      }
    }

    return { left: result1, right: result2 };
  };

  const { owner1, owner2 } = useMemo(() => {
    const data1 =
      generalInfo1?.projectProposalContacts?.find(
        (item) => item?.projectRole?.code === PROJECT_ROLES.OWNER
      ) ?? {};
    const data2 =
      generalInfo2?.projectProposalContacts?.find(
        (item) => item?.projectRole?.code === PROJECT_ROLES.OWNER
      ) ?? {};
    const ownerWithDiffStrings = generateResearchersWithDiffStrings([data1], [data2]);

    return {
      owner1: ownerWithDiffStrings?.left?.[0],
      owner2: ownerWithDiffStrings?.right?.[0],
    };
  }, [generalInfo1, generalInfo2]);

  const lead1 = useMemo(
    () =>
      generalInfo1?.projectProposalOrganizations?.filter(
        (org) => org.type === ORGANIZATION_TYPES.LEAD
      ) ?? [],
    [generalInfo1?.projectProposalOrganizations]
  );
  const lead2 = useMemo(
    () =>
      generalInfo2?.projectProposalOrganizations?.filter(
        (org) => org.type === ORGANIZATION_TYPES.LEAD
      ) ?? [],
    [generalInfo2?.projectProposalOrganizations]
  );
  const partners1 = useMemo(
    () =>
      generalInfo1?.projectProposalOrganizations?.filter(
        (org) => org.type === ORGANIZATION_TYPES.PARTNER
      ) ?? [],
    [generalInfo1?.projectProposalOrganizations]
  );
  const partners2 = useMemo(
    () =>
      generalInfo2?.projectProposalOrganizations?.filter(
        (org) => org.type === ORGANIZATION_TYPES.PARTNER
      ) ?? [],
    [generalInfo2?.projectProposalOrganizations]
  );

  const { scientificSecretaries1, scientificSecretaries2 } = useMemo(() => {
    const data1 =
      generalInfo1?.projectProposalContacts?.filter(
        (item) => item?.projectRole?.code === PROJECT_ROLES.SECRETARY
      ) ?? [];
    const data2 =
      generalInfo2?.projectProposalContacts?.filter(
        (item) => item?.projectRole?.code === PROJECT_ROLES.SECRETARY
      ) ?? [];
    const scientificSecretariesWithDiffStrings = generateResearchersWithDiffStrings(data1, data2);

    return {
      scientificSecretaries1: scientificSecretariesWithDiffStrings?.left,
      scientificSecretaries2: scientificSecretariesWithDiffStrings?.right,
    };
  }, [generalInfo1, generalInfo2]);

  const { mainParticipants1, mainParticipants2 } = useMemo(() => {
    const data1 =
      generalInfo1?.projectProposalContacts?.filter(
        (item) => item?.projectRole?.code === PROJECT_ROLES.MAIN_PARTICIPANT
      ) ?? [];
    const data2 =
      generalInfo2?.projectProposalContacts?.filter(
        (item) => item?.projectRole?.code === PROJECT_ROLES.MAIN_PARTICIPANT
      ) ?? [];
    const mainParticipantsWithDiffStrings = generateResearchersWithDiffStrings(data1, data2);

    return {
      mainParticipants1: mainParticipantsWithDiffStrings?.left,
      mainParticipants2: mainParticipantsWithDiffStrings?.right,
    };
  }, [generalInfo1, generalInfo2]);

  const { participants1, participants2 } = useMemo(() => {
    const data1 =
      generalInfo1?.projectProposalContacts?.filter(
        (item) => item?.projectRole?.code === PROJECT_ROLES.PARTICIPANT
      ) ?? [];
    const data2 =
      generalInfo2?.projectProposalContacts?.filter(
        (item) => item?.projectRole?.code === PROJECT_ROLES.PARTICIPANT
      ) ?? [];
    const participantsWithDiffStrings = generateResearchersWithDiffStrings(data1, data2);

    return {
      participants1: participantsWithDiffStrings?.left,
      participants2: participantsWithDiffStrings?.right,
    };
  }, [generalInfo1, generalInfo2]);

  const { technicians1, technicians2 } = useMemo(() => {
    const data1 =
      generalInfo1?.projectProposalContacts?.filter(
        (item) => item?.projectRole?.code === PROJECT_ROLES.TECHNICIAN
      ) ?? [];
    const data2 =
      generalInfo2?.projectProposalContacts?.filter(
        (item) => item?.projectRole?.code === PROJECT_ROLES.TECHNICIAN
      ) ?? [];
    const techniciansWithDiffStrings = generateResearchersWithDiffStrings(data1, data2);

    return {
      technicians1: techniciansWithDiffStrings?.left,
      technicians2: techniciansWithDiffStrings?.right,
    };
  }, [generalInfo1, generalInfo2]);

  const { domesticExperts1, domesticExperts2 } = useMemo(() => {
    const data1 =
      generalInfo1?.projectProposalContacts?.filter(
        (item) => item?.projectRole?.code === PROJECT_ROLES.DOMESTIC_EXPERT
      ) ?? [];
    const data2 =
      generalInfo2?.projectProposalContacts?.filter(
        (item) => item?.projectRole?.code === PROJECT_ROLES.DOMESTIC_EXPERT
      ) ?? [];
    const domesticExpertsWithDiffStrings = generateResearchersWithDiffStrings(data1, data2);

    return {
      domesticExperts1: domesticExpertsWithDiffStrings?.left,
      domesticExperts2: domesticExpertsWithDiffStrings?.right,
    };
  }, [generalInfo1, generalInfo2]);

  const { internationalExperts1, internationalExperts2 } = useMemo(() => {
    const data1 =
      generalInfo1?.projectProposalContacts?.filter(
        (item) => item?.projectRole?.code === PROJECT_ROLES.INTERNATIONAL_EXPERT
      ) ?? [];
    const data2 =
      generalInfo2?.projectProposalContacts?.filter(
        (item) => item?.projectRole?.code === PROJECT_ROLES.INTERNATIONAL_EXPERT
      ) ?? [];
    const internationalExpertsWithDiffStrings = generateResearchersWithDiffStrings(data1, data2);

    return {
      internationalExperts1: internationalExpertsWithDiffStrings?.left,
      internationalExperts2: internationalExpertsWithDiffStrings?.right,
    };
  }, [generalInfo1, generalInfo2]);

  // region compare
  const diffGeneralInfo = diff(generalInfo1, generalInfo2);
  const diffOwner = diff(owner1, owner2);
  const diffExpenditureInGeneral = diff(expenditureInGeneral1, expenditureInGeneral2);
  const diffLead = diff(lead1, lead2);
  const diffPartners = diff(partners1, partners2);
  const diffScientificSecretary = diff(scientificSecretaries1, scientificSecretaries2);
  const diffMainParticipant = diff(mainParticipants1, mainParticipants2);
  const diffParticipant = diff(participants1, participants2);
  const diffTechnician = diff(technicians1, technicians2);
  const diffDomesticExpert = diff(domesticExperts1, domesticExperts2);
  const diffInternationalExpert = diff(internationalExperts1, internationalExperts2);

  const diffSoftProduct = diff(researchResult1.softProducts, researchResult2.softProducts);
  const diffHardProduct = diff(researchResult1.hardProducts, researchResult2.hardProducts);
  const diffResearchKpi = diff(
    researchResult1.projectProposalResearchKpis,
    researchResult2.projectProposalResearchKpis
  );

  const diffA11 = diff(
    {
      knowledgeContributionLevel: researchResult1?.knowledgeContributionLevel,
      practicalPolicyImpact: researchResult1?.practicalPolicyImpact,
      teamDevelopmentLevel: researchResult1?.teamDevelopmentLevel,
      researchTransferability: researchResult1?.researchTransferability,
    },
    {
      knowledgeContributionLevel: researchResult2?.knowledgeContributionLevel,
      practicalPolicyImpact: researchResult2?.practicalPolicyImpact,
      teamDevelopmentLevel: researchResult2?.teamDevelopmentLevel,
      researchTransferability: researchResult2?.researchTransferability,
    }
  );

  const diffExpenditure = diff(expenditure1, expenditure2);

  const diffDocument = diff(documents1, documents2);

  const { mutate: mutateDownloadFile } = useMutation(downloadFile);

  // #endregion Data

  // #region Event
  const handleDownloadFile = (path, projectProposalIdOfVersion) => {
    mutateDownloadFile(
      { projectProposalId: projectProposalIdOfVersion, path },
      {
        onSuccess: async (res) => {
          if (res?.data) {
            const file = new Blob([res.data]);

            const link = document.createElement('a');
            link.href = URL.createObjectURL(file);
            link.download = formatFileName(path);

            link.click();
          }
        },
      }
    );
  };

  // #endregion Event

  if (isGeneralInfoError1) {
    return (
      <ErrorDialog
        title={t('projectProposal.generalInfoErrorTitle')}
        isError={isGeneralInfoError1}
        error={generalInfoError1}
        visible
        isNavigate
      />
    );
  }
  if (isResearchResultError1) {
    return (
      <ErrorDialog
        title={t('projectProposal.researchResultErrorTitle')}
        isError={isResearchResultError1}
        error={researchResultError1}
        visible
        isNavigate
      />
    );
  }
  if (isExpenditureError1) {
    return (
      <ErrorDialog
        title={t('projectProposal.expenditureErrorTitle')}
        isError={isExpenditureError1}
        error={expenditureError1}
        visible
        isNavigate
      />
    );
  }
  if (isDocumentsError1) {
    return (
      <ErrorDialog
        title={t('projectProposal.documentsErrorTitle')}
        isError={isDocumentsError1}
        error={documentsError1}
        visible
        isNavigate
      />
    );
  }
  if (isGeneralInfoError2) {
    return (
      <ErrorDialog
        title={t('projectProposal.generalInfoErrorTitle')}
        isError={isGeneralInfoError2}
        error={generalInfoError2}
        visible
        isNavigate
      />
    );
  }
  if (isResearchResultError2) {
    return (
      <ErrorDialog
        title={t('projectProposal.researchResultErrorTitle')}
        isError={isResearchResultError2}
        error={researchResultError2}
        visible
        isNavigate
      />
    );
  }
  if (isExpenditureError2) {
    return (
      <ErrorDialog
        title={t('projectProposal.expenditureErrorTitle')}
        isError={isExpenditureError2}
        error={expenditureError2}
        visible
        isNavigate
      />
    );
  }
  if (isDocumentsError2) {
    return (
      <ErrorDialog
        title={t('projectProposal.documentsErrorTitle')}
        isError={isDocumentsError2}
        error={documentsError2}
        visible
        isNavigate
      />
    );
  }

  return (
    <div className="bg-white p-5">
      {(isGeneralInfoLoading1 ||
        isGeneralInfoLoading2 ||
        isResearchResultLoading1 ||
        isResearchResultLoading2 ||
        isExpenditureLoading1 ||
        isExpenditureLoading2 ||
        isDocumentsLoading1 ||
        isDocumentsLoading2) && <Loading />}
      <div className="m-0 bg-white p-fluid detail-info-project-proposal">
        <table className="compare-table w-full">
          <thead>
            <tr>
              <th className="py-2" style={{ width: '49%', maxWidth: '49%' }}>
                {mapVersionName[previousVersion]}
              </th>
              <th className="py-2" style={{ width: '49%', maxWidth: '49%' }}>
                {mapVersionName[version]}
              </th>
              <th className="py-2" style={{ minWidth: '10px', maxWidth: '2%' }}>
                {' '}
              </th>
            </tr>
          </thead>
          <tbody>
            {/* A1 */}
            <tr className="compare-title">
              <td colSpan={3}>
                <h4>{t('projectProposal.general.viewLabel')}</h4>
                <h4 className="mb-2 ">{t('projectProposal.general.projectNameAndType.label')}</h4>
              </td>
            </tr>
            <tr>
              <td>
                <p className="bg-black">
                  <span className="mr-2 font-semibold">
                    {t('projectProposal.general.projectNameAndType.vietnameseName')}:
                  </span>
                  {
                    generateDiffStrings(generalInfo1?.vietnameseName, generalInfo2?.vietnameseName)
                      ?.left
                  }
                </p>
              </td>
              <td>
                <span className="mr-2 font-semibold">
                  {t('projectProposal.general.projectNameAndType.vietnameseName')}:
                </span>
                <span>
                  {
                    generateDiffStrings(generalInfo1?.vietnameseName, generalInfo2?.vietnameseName)
                      ?.right
                  }
                </span>
              </td>
              <DiffColumn
                isDifferent={diffGeneralInfo?.vietnameseName}
                content={renderDiffString(
                  generalInfo1?.vietnameseName,
                  generalInfo2?.vietnameseName
                )}
              />
            </tr>
            <tr>
              <td>
                <p>
                  <span className="mr-2 font-semibold">
                    {t('projectProposal.general.projectNameAndType.englishName')}:
                  </span>
                  {generateDiffStrings(generalInfo1?.englishName, generalInfo2?.englishName)?.left}
                </p>
              </td>
              <td>
                <span className="mr-2 font-semibold">
                  {t('projectProposal.general.projectNameAndType.englishName')}:
                </span>
                <span>
                  {generateDiffStrings(generalInfo1?.englishName, generalInfo2?.englishName)?.right}
                </span>
              </td>
              <DiffColumn
                isDifferent={diffGeneralInfo?.englishName}
                content={renderDiffString(generalInfo1?.englishName, generalInfo2?.englishName)}
              />
            </tr>
            <tr>
              <td>
                <p>
                  <span className="mr-2 font-semibold">
                    {t('projectProposal.general.projectNameAndType.projectType')}:{' '}
                  </span>
                  {
                    generateDiffStrings(
                      t(`projectTypes.${generalInfo1?.projectType}`),
                      t(`projectTypes.${generalInfo2?.projectType}`)
                    )?.left
                  }
                </p>
              </td>
              <td>
                <p>
                  <span className="mr-2 font-semibold">
                    {t('projectProposal.general.projectNameAndType.projectType')}:{' '}
                  </span>
                  <span>
                    {
                      generateDiffStrings(
                        t(`projectTypes.${generalInfo1?.projectType}`),
                        t(`projectTypes.${generalInfo2?.projectType}`)
                      )?.right
                    }
                  </span>
                </p>
              </td>
              <DiffColumn isDifferent={t(`projectTypes.${diffGeneralInfo?.projectType}`)} />
            </tr>
            {/* A2 */}
            <tr className="compare-title">
              <td colSpan={3}>
                <h4>{t('projectProposal.general.majorsOrMajorsGroup.label')}</h4>
              </td>
            </tr>
            {Array(
              Math.max(
                generalInfo1?.projectProposalResearchFields?.length ?? 0,
                generalInfo2.projectProposalResearchFields?.length ?? 0
              )
            )
              ?.fill(null)
              ?.map((_, i) => (
                // eslint-disable-next-line react/no-array-index-key
                <tr key={i}>
                  <td>
                    <div
                      className="flex ml-5 text-lg mb-2"
                      key={generalInfo1?.projectProposalResearchFields[i]?.researchField.id}
                    >
                      <div className="mr-2 font-semibold">
                        {t('projectProposal.general.majorsOrMajorsGroup.prio.researchField')}{' '}
                        {generalInfo1?.projectProposalResearchFields[i]?.priority ?? i + 1}:{' '}
                      </div>
                      <div className="w-10rem mr-4">
                        {
                          generateDiffStrings(
                            generalInfo1?.projectProposalResearchFields[i]?.researchField?.code
                              ? t(
                                  `researchFields.${generalInfo1?.projectProposalResearchFields[i]?.researchField?.code}`
                                )
                              : generalInfo1?.projectProposalResearchFields[i]?.researchField?.name,
                            generalInfo2?.projectProposalResearchFields[i]?.researchField?.code
                              ? t(
                                  `researchFields.${generalInfo2?.projectProposalResearchFields[i]?.researchField?.code}`
                                )
                              : generalInfo2?.projectProposalResearchFields[i]?.researchField?.name
                          )?.left
                        }
                      </div>
                      <div className="font-semibold mr-2 ">
                        {t('projectProposal.general.majorsOrMajorsGroup.researchLine')}:{' '}
                      </div>
                      <div className="flex-1">
                        {
                          generateDiffStrings(
                            generalInfo1?.projectProposalResearchFields[i]?.researchLine,
                            generalInfo2?.projectProposalResearchFields[i]?.researchLine
                          )?.left
                        }
                      </div>
                    </div>
                  </td>

                  <td>
                    <div
                      className="flex ml-5 text-lg mb-2"
                      key={generalInfo2?.projectProposalResearchFields[i]?.researchField.id}
                    >
                      <div className="mr-2 font-semibold">
                        {t('projectProposal.general.majorsOrMajorsGroup.prio.researchField')}{' '}
                        {generalInfo2?.projectProposalResearchFields[i]?.priority ?? i + 1}:{' '}
                      </div>
                      <div className="w-10rem mr-4">
                        {
                          generateDiffStrings(
                            generalInfo1?.projectProposalResearchFields[i]?.researchField?.code
                              ? t(
                                  `researchFields.${generalInfo1?.projectProposalResearchFields[i]?.researchField?.code}`
                                )
                              : generalInfo1?.projectProposalResearchFields[i]?.researchField?.name,
                            generalInfo2?.projectProposalResearchFields[i]?.researchField?.code
                              ? t(
                                  `researchFields.${generalInfo2?.projectProposalResearchFields[i]?.researchField?.code}`
                                )
                              : generalInfo2?.projectProposalResearchFields[i]?.researchField?.name
                          )?.right
                        }
                      </div>
                      <div className="font-semibold mr-2 ">
                        {t('projectProposal.general.majorsOrMajorsGroup.researchLine')}:{' '}
                      </div>
                      <div className="flex-1">
                        <span>
                          {
                            generateDiffStrings(
                              generalInfo1?.projectProposalResearchFields[i]?.researchLine,
                              generalInfo2?.projectProposalResearchFields[i]?.researchLine
                            )?.right
                          }
                        </span>
                      </div>
                    </div>
                  </td>
                  <DiffColumn
                    isDifferent={
                      diffGeneralInfo?.projectProposalResearchFields[i] === true ||
                      diffGeneralInfo?.projectProposalResearchFields[i]?.researchField?.code ||
                      diffGeneralInfo?.projectProposalResearchFields[i]?.researchLine
                    }
                  />
                </tr>
              ))}
            <tr className="compare-title">
              <td colSpan={3}>
                <h4>{t('projectProposal.general.recommendedExpert.label')}</h4>
              </td>
            </tr>
            <RecommendedExpertCompareTable
              data1={generalInfo1?.recommendedExperts}
              data2={generalInfo2?.recommendedExperts}
              diffObj={diffGeneralInfo?.recommendedExperts}
            />
            {/* A3 */}
            <tr className="compare-title">
              <td colSpan={3}>
                <h4>{t('projectProposal.general.researchType.label')}</h4>
              </td>
            </tr>
            <tr>
              <td>
                <p className="flex-1 ml-5">
                  <span className="font-semibold">
                    {t('projectProposal.general.researchType.researchType')}:{' '}
                  </span>
                  {
                    generateDiffStrings(
                      generalInfo1?.researchType?.code
                        ? t(`researchTypes.${generalInfo1?.researchType?.code}`)
                        : generalInfo1?.researchType?.name,
                      generalInfo2?.researchType?.code
                        ? t(`researchTypes.${generalInfo2?.researchType?.code}`)
                        : generalInfo2?.researchType?.name
                    )?.left
                  }
                </p>
              </td>
              <td>
                <p className="flex-1 ml-5">
                  <span className="font-semibold">
                    {t('projectProposal.general.researchType.researchType')}:{' '}
                  </span>
                  <span>
                    {
                      generateDiffStrings(
                        generalInfo1?.researchType?.code
                          ? t(`researchTypes.${generalInfo1?.researchType?.code}`)
                          : generalInfo1?.researchType?.name,
                        generalInfo2?.researchType?.code
                          ? t(`researchTypes.${generalInfo2?.researchType?.code}`)
                          : generalInfo2?.researchType?.name
                      )?.right
                    }
                  </span>
                </p>
              </td>
              <DiffColumn isDifferent={diffGeneralInfo?.researchType?.code} />
            </tr>
            {/* A4 */}
            <tr className="compare-title">
              <td colSpan={3}>
                <h4>{t('projectProposal.general.researchTime.label')}</h4>
              </td>
            </tr>
            <tr>
              <td>
                <p className="flex-1 ml-5 mt-1">
                  <span className="font-semibold">
                    {t('projectProposal.general.researchTime.researchTime')}:{' '}
                  </span>
                  {
                    generateDiffStrings(
                      `${generalInfo1?.implementationMonths ?? ''}`,
                      `${generalInfo2?.implementationMonths ?? ''}`
                    )?.left
                  }
                </p>
              </td>
              <td>
                <p className="flex-1 ml-5 mt-1">
                  <span className="font-semibold">
                    {t('projectProposal.general.researchTime.researchTime')}:{' '}
                  </span>
                  <span>
                    {
                      generateDiffStrings(
                        `${generalInfo1?.implementationMonths ?? ''}`,
                        `${generalInfo2?.implementationMonths ?? ''}`
                      )?.right
                    }
                  </span>
                </p>
              </td>
              <DiffColumn isDifferent={diffGeneralInfo?.implementationMonths} />
            </tr>
            {/* A5 */}
            <tr className="compare-title">
              <td colSpan={3}>
                <h4>{t('projectProposal.general.totalExpenditure.label')}</h4>
              </td>
            </tr>
            <tr>
              <td>
                <div className="ml-5">
                  <span className="font-semibold">
                    {t('projectProposal.general.totalExpenditure.total')}:{' '}
                  </span>
                  {
                    generateDiffStrings(
                      expenditureInGeneral1?.totalExpenditure?.toLocaleString(
                        'vi',
                        currencyOptions
                      ),
                      expenditureInGeneral2?.totalExpenditure?.toLocaleString('vi', currencyOptions)
                    )?.left
                  }
                  , {t('projectProposal.general.totalExpenditure.includes')}:
                </div>
              </td>
              <td>
                <div className="ml-5">
                  <span className="font-semibold">
                    {t('projectProposal.general.totalExpenditure.total')}:{' '}
                  </span>
                  <span>
                    {
                      generateDiffStrings(
                        expenditureInGeneral1?.totalExpenditure?.toLocaleString(
                          'vi',
                          currencyOptions
                        ),
                        expenditureInGeneral2?.totalExpenditure?.toLocaleString(
                          'vi',
                          currencyOptions
                        )
                      )?.right
                    }
                  </span>
                  , {t('projectProposal.general.totalExpenditure.includes')}:
                </div>
              </td>
              <DiffColumn isDifferent={diffExpenditureInGeneral?.totalExpenditure} />
            </tr>
            <tr>
              <td>
                <div className="mt-1 ml-7">
                  <span className="font-semibold">
                    {t('projectProposal.general.totalExpenditure.fromVnu')}:{' '}
                  </span>
                  {
                    generateDiffStrings(
                      expenditureInGeneral1?.nationalUniversityTotalExpenditure?.toLocaleString(
                        'vi',
                        currencyOptions
                      ),
                      expenditureInGeneral2?.nationalUniversityTotalExpenditure?.toLocaleString(
                        'vi',
                        currencyOptions
                      )
                    )?.left
                  }
                </div>
              </td>
              <td>
                <div className="mt-1 ml-7">
                  <span className="font-semibold">
                    {t('projectProposal.general.totalExpenditure.fromVnu')}:{' '}
                  </span>
                  <span>
                    {
                      generateDiffStrings(
                        expenditureInGeneral1?.nationalUniversityTotalExpenditure?.toLocaleString(
                          'vi',
                          currencyOptions
                        ),
                        expenditureInGeneral2?.nationalUniversityTotalExpenditure?.toLocaleString(
                          'vi',
                          currencyOptions
                        )
                      )?.right
                    }
                  </span>
                </div>
              </td>
              <DiffColumn
                isDifferent={diffExpenditureInGeneral?.nationalUniversityTotalExpenditure}
              />
            </tr>
            <tr>
              <td>
                <div className="mt-1 ml-7 font-italic">
                  {t('projectProposal.general.totalExpenditure.inParticular')}:
                </div>
                <div className="ml-8 font-italic">
                  <div className="mt-1 ml-2">
                    <span className="font-semibold">
                      {t('projectProposal.general.totalExpenditure.allocatedAdministrative')}
                    </span>
                    [
                    <span style={{ color: 'red' }}>
                      {t('projectProposal.general.totalExpenditure.item')} 1 +{' '}
                      {t('projectProposal.general.totalExpenditure.item')} 3 +{' '}
                      {t('projectProposal.general.totalExpenditure.item')} 4
                    </span>
                    ]:{' '}
                    {
                      generateDiffStrings(
                        expenditureInGeneral1?.nationalUniversityAllocatedExpenditure?.toLocaleString(
                          'vi',
                          currencyOptions
                        ),
                        expenditureInGeneral2?.nationalUniversityAllocatedExpenditure?.toLocaleString(
                          'vi',
                          currencyOptions
                        )
                      )?.left
                    }
                  </div>
                  <div className="mt-1 ml-2">
                    <span className="font-semibold">
                      {t('projectProposal.general.totalExpenditure.nonAllocatedAdministrative')}
                    </span>
                    [{t('projectProposal.general.totalExpenditure.item')} 2]:{' '}
                    {
                      generateDiffStrings(
                        expenditureInGeneral1?.nationalUniversityNonallocatedExpenditure?.toLocaleString(
                          'vi',
                          currencyOptions
                        ),
                        expenditureInGeneral2?.nationalUniversityNonallocatedExpenditure?.toLocaleString(
                          'vi',
                          currencyOptions
                        )
                      )?.left
                    }{' '}
                  </div>
                </div>
              </td>
              <td>
                <div className="mt-1 ml-7 font-italic">
                  {t('projectProposal.general.totalExpenditure.inParticular')}:
                </div>
                <div className="ml-8 font-italic">
                  <div className="mt-1 ml-2">
                    <span className="font-semibold">
                      {t('projectProposal.general.totalExpenditure.allocatedAdministrative')}
                    </span>
                    [
                    <span style={{ color: 'red' }}>
                      {t('projectProposal.general.totalExpenditure.item')} 1 +{' '}
                      {t('projectProposal.general.totalExpenditure.item')} 3 +{' '}
                      {t('projectProposal.general.totalExpenditure.item')} 4
                    </span>
                    ]:{' '}
                    <span>
                      {
                        generateDiffStrings(
                          expenditureInGeneral1?.nationalUniversityAllocatedExpenditure?.toLocaleString(
                            'vi',
                            currencyOptions
                          ),
                          expenditureInGeneral2?.nationalUniversityAllocatedExpenditure?.toLocaleString(
                            'vi',
                            currencyOptions
                          )
                        )?.right
                      }
                    </span>
                  </div>
                  <div className="mt-1 ml-2">
                    <span className="font-semibold">
                      {t('projectProposal.general.totalExpenditure.nonAllocatedAdministrative')}
                    </span>
                    [{t('projectProposal.general.totalExpenditure.item')} 2]:{' '}
                    <span>
                      {
                        generateDiffStrings(
                          expenditureInGeneral1?.nationalUniversityNonallocatedExpenditure?.toLocaleString(
                            'vi',
                            currencyOptions
                          ),
                          expenditureInGeneral2?.nationalUniversityNonallocatedExpenditure?.toLocaleString(
                            'vi',
                            currencyOptions
                          )
                        )?.right
                      }{' '}
                    </span>
                  </div>
                </div>
              </td>
              <DiffColumn
                isDifferent={
                  diffExpenditureInGeneral?.nationalUniversityAllocatedExpenditure ||
                  diffExpenditureInGeneral?.nationalUniversityNonallocatedExpenditure
                }
              />
            </tr>
            <tr>
              <td>
                <div className="mt-1 ml-7">
                  <span className="font-semibold">
                    {t('projectProposal.general.totalExpenditure.fromFunding')}:{' '}
                  </span>
                  {
                    generateDiffStrings(
                      expenditureInGeneral1?.fundingExpenditure?.toLocaleString(
                        'vi',
                        currencyOptions
                      ),
                      expenditureInGeneral2?.fundingExpenditure?.toLocaleString(
                        'vi',
                        currencyOptions
                      )
                    )?.left
                  }
                  ,
                </div>
              </td>
              <td>
                <div className="mt-1 ml-7">
                  <span className="font-semibold">
                    {t('projectProposal.general.totalExpenditure.fromFunding')}:{' '}
                  </span>
                  <span>
                    {
                      generateDiffStrings(
                        expenditureInGeneral1?.fundingExpenditure?.toLocaleString(
                          'vi',
                          currencyOptions
                        ),
                        expenditureInGeneral2?.fundingExpenditure?.toLocaleString(
                          'vi',
                          currencyOptions
                        )
                      )?.right
                    }
                    ,
                  </span>
                </div>
              </td>
              <DiffColumn isDifferent={diffExpenditureInGeneral?.fundingExpenditure} />
            </tr>
            <tr>
              <td>
                <div className="mt-1 ml-7 font-italic">
                  {t('projectProposal.general.totalExpenditure.inParticular')}:
                </div>
                <div className="flex text-lg ml-6 font-italic">
                  <div className="mt-1 ml-6">
                    <span className="font-semibold">
                      {t('projectProposal.general.totalExpenditure.ownCapital')}:{' '}
                    </span>
                    {
                      generateDiffStrings(
                        expenditureInGeneral1?.fundingExpenditureSelf?.toLocaleString(
                          'vi',
                          currencyOptions
                        ),
                        expenditureInGeneral2?.fundingExpenditureSelf?.toLocaleString(
                          'vi',
                          currencyOptions
                        )
                      )?.left
                    }
                  </div>
                </div>
                <div className="flex text-lg mb-2 ml-6 font-italic">
                  <div className="mt-1 ml-6">
                    <span className="font-semibold">
                      {t('projectProposal.general.totalExpenditure.otherCapital')}:{' '}
                    </span>
                    {
                      generateDiffStrings(
                        expenditureInGeneral1?.fundingExpenditureOther?.toLocaleString(
                          'vi',
                          currencyOptions
                        ),
                        expenditureInGeneral2?.fundingExpenditureOther?.toLocaleString(
                          'vi',
                          currencyOptions
                        )
                      )?.left
                    }
                  </div>
                </div>
              </td>
              <td>
                <div className="mt-1 ml-7 font-italic">
                  {t('projectProposal.general.totalExpenditure.inParticular')}:
                </div>
                <div className="flex text-lg ml-6 font-italic">
                  <div className="mt-1 ml-6">
                    <span className="font-semibold">
                      {t('projectProposal.general.totalExpenditure.ownCapital')}:{' '}
                    </span>
                    <span className={highlight(diffExpenditureInGeneral?.fundingExpenditureSelf)}>
                      {
                        generateDiffStrings(
                          expenditureInGeneral1?.fundingExpenditureSelf?.toLocaleString(
                            'vi',
                            currencyOptions
                          ),
                          expenditureInGeneral2?.fundingExpenditureSelf?.toLocaleString(
                            'vi',
                            currencyOptions
                          )
                        )?.right
                      }
                    </span>
                  </div>
                </div>
                <div className="flex text-lg mb-2 ml-6 font-italic">
                  <div className="mt-1 ml-6">
                    <span className="font-semibold">
                      {t('projectProposal.general.totalExpenditure.otherCapital')}:{' '}
                    </span>
                    <span>
                      {
                        generateDiffStrings(
                          expenditureInGeneral1?.fundingExpenditureOther?.toLocaleString(
                            'vi',
                            currencyOptions
                          ),
                          expenditureInGeneral2?.fundingExpenditureOther?.toLocaleString(
                            'vi',
                            currencyOptions
                          )
                        )?.right
                      }
                    </span>
                  </div>
                </div>
              </td>
              <DiffColumn
                isDifferent={
                  diffExpenditureInGeneral?.fundingExpenditureSelf ||
                  diffExpenditureInGeneral?.fundingExpenditureOther
                }
              />
            </tr>
            <tr>
              <td>
                <div className="mb-2 ml-5">
                  <div>
                    <span className="font-semibold">
                      {t('projectProposal.general.totalExpenditure.otherFunding')}:{' '}
                    </span>
                  </div>
                  <p>
                    {
                      generateDiffStrings(
                        generalInfo1?.otherSponsorOrganization,
                        generalInfo2?.otherSponsorOrganization
                      )?.left
                    }
                  </p>
                </div>
              </td>
              <td>
                <div className="mb-2 ml-5">
                  <div>
                    <span className="font-semibold">
                      {t('projectProposal.general.totalExpenditure.otherFunding')}:{' '}
                    </span>
                  </div>
                  <p>
                    {
                      generateDiffStrings(
                        generalInfo1?.otherSponsorOrganization,
                        generalInfo2?.otherSponsorOrganization
                      )?.right
                    }
                  </p>
                </div>
              </td>
              <DiffColumn isDifferent={diffGeneralInfo?.otherSponsorOrganization} />
            </tr>
            <tr>
              <td>
                <div className="mb-2 ml-5">
                  <span className="font-semibold">
                    {t('projectProposal.expenditure.expenditure.label')}:
                  </span>
                </div>
              </td>
              <td>
                <div className="mb-2 ml-5">
                  <span className="font-semibold">
                    {t('projectProposal.expenditure.expenditure.label')}:
                  </span>
                </div>
              </td>
              <td />
            </tr>
            <ExpenditureCompareTable
              expenditure1={expenditure1}
              expenditure2={expenditure2}
              diffObj={diffExpenditure}
            />

            {/* A6 */}
            <tr className="compare-title">
              <td colSpan={3}>
                <h4>{t('projectProposal.general.ownerSection.label')}</h4>
              </td>
            </tr>
            <tr>
              <td>
                <div className="flex flex-wrap">
                  <div className="w-30rem mb-2">
                    <span className="font-semibold">
                      {t('projectProposal.general.ownerSection.owner.academicRank')}:{' '}
                    </span>
                    <span>
                      {
                        generateDiffStrings(
                          owner1?.academicRank ? t(`academicRank.${owner1?.academicRank}`) : '',
                          owner2?.academicRank ? t(`academicRank.${owner2?.academicRank}`) : ''
                        )?.left
                      }
                    </span>
                  </div>
                  <div className="mb-2">
                    <span className="font-semibold">
                      {t('projectProposal.general.ownerSection.owner.academicDegree')}:{' '}
                    </span>
                    <span>
                      {
                        generateDiffStrings(
                          owner1?.academicDegree
                            ? t(`academicDegree.${owner1?.academicDegree}`)
                            : '',
                          owner2?.academicDegree
                            ? t(`academicDegree.${owner2?.academicDegree}`)
                            : ''
                        )?.left
                      }
                    </span>
                  </div>
                </div>
              </td>
              <td>
                <div className="flex flex-wrap">
                  <div className="w-30rem mb-2">
                    <span className="font-semibold">
                      {t('projectProposal.general.ownerSection.owner.academicRank')}:{' '}
                    </span>
                    <span>
                      {
                        generateDiffStrings(
                          owner1?.academicRank ? t(`academicRank.${owner1?.academicRank}`) : '',
                          owner2?.academicRank ? t(`academicRank.${owner2?.academicRank}`) : ''
                        )?.right
                      }
                    </span>
                  </div>
                  <div className="mb-2">
                    <span className="font-semibold">
                      {t('projectProposal.general.ownerSection.owner.academicDegree')}:{' '}
                    </span>
                    <span>
                      {
                        generateDiffStrings(
                          owner1?.academicDegree
                            ? t(`academicDegree.${owner1?.academicDegree}`)
                            : '',
                          owner2?.academicDegree
                            ? t(`academicDegree.${owner2?.academicDegree}`)
                            : ''
                        )?.right
                      }
                    </span>
                  </div>
                </div>
              </td>
              <DiffColumn isDifferent={diffOwner?.academicRank || diffOwner?.academicDegree} />
            </tr>
            <tr>
              <td>
                <div className="mb-2">
                  <span className="font-semibold">
                    {t('projectProposal.general.ownerSection.owner.name')}:{' '}
                  </span>
                  <span>
                    {
                      generateDiffStrings(
                        owner1?.fullname ?? generalInfo1?.owner?.fullname,
                        owner2?.fullname ?? generalInfo2?.owner?.fullname
                      )?.left
                    }
                  </span>
                </div>
              </td>
              <td className={highlight(diffGeneralInfo?.owner?.fullname)}>
                <div className="mb-2">
                  <span className="font-semibold">
                    {t('projectProposal.general.ownerSection.owner.name')}:{' '}
                  </span>
                  <span>
                    {
                      generateDiffStrings(
                        owner1?.fullname ?? generalInfo1?.owner?.fullname,
                        owner2?.fullname ?? generalInfo2?.owner?.fullname
                      )?.right
                    }
                  </span>
                </div>
              </td>
              <DiffColumn
                isDifferent={diffOwner?.fullname}
                content={renderDiffString(owner1?.fullname, owner2?.fullname)}
              />
            </tr>
            <tr>
              <td>
                <div className="mb-2">
                  <span className="font-semibold">
                    {t('projectProposal.general.ownerSection.owner.dob')}:{' '}
                  </span>
                  <span>
                    {
                      generateDiffStrings(
                        owner1?.dob
                          ? new Date(owner1?.dob).toLocaleDateString('en-GB', {
                              timeZone: TIMEZONE_VN,
                            })
                          : '',
                        owner2?.dob
                          ? new Date(owner2?.dob).toLocaleDateString('en-GB', {
                              timeZone: TIMEZONE_VN,
                            })
                          : ''
                      )?.left
                    }
                  </span>
                </div>
              </td>
              <td>
                <div className="mb-2">
                  <span className="font-semibold">
                    {t('projectProposal.general.ownerSection.owner.dob')}:{' '}
                  </span>
                  <span className={highlight(diffOwner?.dob)}>
                    {
                      generateDiffStrings(
                        owner1?.dob
                          ? new Date(owner1?.dob).toLocaleDateString('en-GB', {
                              timeZone: TIMEZONE_VN,
                            })
                          : '',
                        owner2?.dob
                          ? new Date(owner2?.dob).toLocaleDateString('en-GB', {
                              timeZone: TIMEZONE_VN,
                            })
                          : ''
                      )?.right
                    }
                  </span>
                </div>
              </td>
              <DiffColumn isDifferent={diffOwner?.dob} />
            </tr>
            <tr>
              <td>
                <div className="flex flex-wrap">
                  <div className="w-30rem mb-2">
                    <span className="font-semibold">
                      {t('projectProposal.general.ownerSection.owner.citizenId')}:{' '}
                    </span>
                    <span>{generateDiffStrings(owner1?.citizenId, owner2?.citizenId)?.left}</span>
                  </div>
                  <div className="w-20rem mb-2">
                    <span className="font-semibold">
                      {t('projectProposal.general.ownerSection.owner.placeOfIssue')}:{' '}
                    </span>
                    <span>
                      {generateDiffStrings(owner1?.placeOfIssue, owner2?.placeOfIssue)?.left}
                    </span>
                  </div>
                  <div className="mb-2">
                    <span className="font-semibold">
                      {t('projectProposal.general.ownerSection.owner.dateOfIssue')}:{' '}
                    </span>
                    <span>
                      {
                        generateDiffStrings(
                          owner1?.dateOfIssue
                            ? new Date(owner1?.dateOfIssue).toLocaleDateString('en-GB', {
                                timeZone: TIMEZONE_VN,
                              })
                            : '',
                          owner2?.dateOfIssue
                            ? new Date(owner2?.dateOfIssue).toLocaleDateString('en-GB', {
                                timeZone: TIMEZONE_VN,
                              })
                            : ''
                        )?.left
                      }
                    </span>
                  </div>
                </div>
              </td>
              <td>
                <div className="flex flex-wrap">
                  <div className="w-30rem mb-2">
                    <span className="font-semibold">
                      {t('projectProposal.general.ownerSection.owner.citizenId')}:{' '}
                    </span>
                    <span>{generateDiffStrings(owner1?.citizenId, owner2?.citizenId)?.right}</span>
                  </div>
                  <div className="w-20rem mb-2">
                    <span className="font-semibold">
                      {t('projectProposal.general.ownerSection.owner.placeOfIssue')}:{' '}
                    </span>
                    <span>
                      {generateDiffStrings(owner1?.placeOfIssue, owner2?.placeOfIssue)?.right}
                    </span>
                  </div>
                  <div className="mb-2">
                    <span className="font-semibold">
                      {t('projectProposal.general.ownerSection.owner.dateOfIssue')}:{' '}
                    </span>
                    <span>
                      {
                        generateDiffStrings(
                          owner1?.dateOfIssue
                            ? new Date(owner1?.dateOfIssue).toLocaleDateString('en-GB', {
                                timeZone: TIMEZONE_VN,
                              })
                            : '',
                          owner2?.dateOfIssue
                            ? new Date(owner2?.dateOfIssue).toLocaleDateString('en-GB', {
                                timeZone: TIMEZONE_VN,
                              })
                            : ''
                        )?.right
                      }
                    </span>
                  </div>
                </div>
              </td>
              <DiffColumn
                isDifferent={
                  diffOwner?.citizenId || diffOwner?.placeOfIssue || diffOwner?.dateOfIssue
                }
              />
            </tr>
            <tr>
              <td>
                <div className="mb-2">
                  <span className="font-semibold">
                    {t('projectProposal.general.ownerSection.owner.taxIdentificationNumber')}:{' '}
                  </span>
                  <span>
                    {
                      generateDiffStrings(
                        owner1?.taxIdentificationNumber,
                        owner2?.taxIdentificationNumber
                      )?.left
                    }
                  </span>
                </div>
              </td>
              <td>
                <div className="mb-2">
                  <span className="font-semibold">
                    {t('projectProposal.general.ownerSection.owner.taxIdentificationNumber')}:{' '}
                  </span>
                  <span>
                    {
                      generateDiffStrings(
                        owner1?.taxIdentificationNumber,
                        owner2?.taxIdentificationNumber
                      )?.right
                    }
                  </span>
                </div>
              </td>
              <DiffColumn isDifferent={diffOwner?.taxIdentificationNumber} />
            </tr>
            <tr>
              <td>
                <div className="flex flex-wrap">
                  <div className="w-30rem mb-2">
                    <span className="font-semibold">
                      {t('projectProposal.general.ownerSection.owner.bankAccount')}:{' '}
                    </span>
                    <span>
                      {
                        generateDiffStrings(
                          owner1?.bankAccount ?? generalInfo1?.bankAccount,
                          owner2?.bankAccount ?? generalInfo2?.bankAccount
                        )?.left
                      }
                    </span>
                  </div>
                  <div className="mb-2">
                    <span className="font-semibold">
                      {t('projectProposal.general.ownerSection.owner.bank')}:{' '}
                    </span>
                    <span>
                      {
                        generateDiffStrings(
                          owner1?.bank ?? generalInfo1?.bank,
                          owner2?.bank ?? generalInfo2?.bank
                        )?.left
                      }
                    </span>
                  </div>
                </div>
              </td>
              <td>
                <div className="flex flex-wrap">
                  <div className="w-30rem mb-2">
                    <span className="font-semibold">
                      {t('projectProposal.general.ownerSection.owner.bankAccount')}:{' '}
                    </span>
                    <span>
                      {
                        generateDiffStrings(
                          owner1?.bankAccount ?? generalInfo1?.bankAccount,
                          owner2?.bankAccount ?? generalInfo2?.bankAccount
                        )?.right
                      }
                    </span>
                  </div>
                  <div className="mb-2">
                    <span className="font-semibold">
                      {t('projectProposal.general.ownerSection.owner.bank')}:{' '}
                    </span>
                    <span>
                      {
                        generateDiffStrings(
                          owner1?.bank ?? generalInfo1?.bank,
                          owner2?.bank ?? generalInfo2?.bank
                        )?.right
                      }
                    </span>
                  </div>
                </div>
              </td>
              <DiffColumn isDifferent={diffOwner?.bankAccount || diffOwner?.bank} />
            </tr>
            <tr>
              <td>
                <div className="flex flex-wrap">
                  <div className="mb-2">
                    <span className="font-semibold">
                      {t('projectProposal.general.ownerSection.owner.workplace')}:{' '}
                    </span>
                    <span>
                      {generateDiffStrings(generalInfo1?.workplace, generalInfo2?.workplace)?.left}
                    </span>
                  </div>
                </div>
              </td>
              <td>
                <div className="flex flex-wrap">
                  <div className="mb-2">
                    <span className="font-semibold">
                      {t('projectProposal.general.ownerSection.owner.workplace')}:{' '}
                    </span>
                    <span>
                      {generateDiffStrings(generalInfo1?.workplace, generalInfo2?.workplace)?.right}
                    </span>
                  </div>
                </div>
              </td>
              <DiffColumn isDifferent={diffGeneralInfo?.workplace} />
            </tr>
            <tr>
              <td>
                <div className="flex flex-wrap">
                  <div className="w-30rem mb-2">
                    <span className="font-semibold">
                      {t('projectProposal.general.ownerSection.owner.mobile')}:{' '}
                    </span>
                    <span>
                      {
                        generateDiffStrings(
                          owner1?.mobile ?? generalInfo1?.mobile,
                          owner2?.mobile ?? generalInfo2?.mobile
                        )?.left
                      }
                    </span>
                  </div>
                  <div className="mb-2">
                    <span className="font-semibold">Email: </span>
                    <span>
                      {
                        generateDiffStrings(
                          owner1?.email ?? generalInfo1?.email,
                          owner2?.email ?? generalInfo2?.email
                        )?.left
                      }
                    </span>
                  </div>
                </div>
              </td>
              <td>
                <div className="flex flex-wrap">
                  <div className="w-30rem mb-2">
                    <span className="font-semibold">
                      {t('projectProposal.general.ownerSection.owner.mobile')}:{' '}
                    </span>
                    <span>
                      {
                        generateDiffStrings(
                          owner1?.mobile ?? generalInfo1?.mobile,
                          owner2?.mobile ?? generalInfo2?.mobile
                        )?.right
                      }
                    </span>
                  </div>
                  <div className="mb-2">
                    <span className="font-semibold">Email: </span>
                    <span className={highlight(diffOwner?.email)}>
                      {
                        generateDiffStrings(
                          owner1?.email ?? generalInfo1?.email,
                          owner2?.email ?? generalInfo2?.email
                        )?.right
                      }
                    </span>
                  </div>
                </div>
              </td>
              <DiffColumn isDifferent={diffOwner?.mobile || diffOwner?.email} />
            </tr>
            <tr>
              <td>
                <div className="mb-2">
                  <div>
                    <span className="font-semibold">
                      {t('projectProposal.general.ownerSection.owner.summarizedExperience')}:{' '}
                    </span>
                  </div>
                  <p>
                    {(owner1?.experience || generalInfo1?.experience) && (
                      <RichTextViewer
                        value={owner1?.experience || generalInfo1?.experience || ''}
                        showHeader={false}
                        readOnly
                      />
                    )}
                  </p>
                </div>
              </td>
              <td>
                <div className="mb-2">
                  <div>
                    <span className="font-semibold">
                      {t('projectProposal.general.ownerSection.owner.summarizedExperience')}:{' '}
                    </span>
                  </div>
                  <p>
                    {(owner2?.experience || generalInfo2?.experience) && (
                      <RichTextViewer
                        value={owner2?.experience || generalInfo2?.experience || ''}
                        showHeader={false}
                        readOnly
                      />
                    )}
                  </p>
                </div>
              </td>
              <DiffColumn
                isDifferent={
                  (owner1?.experience || generalInfo1?.experience || '') !==
                  (owner2?.experience || generalInfo2?.experience || '')
                }
                content={renderDiffString(
                  owner1?.experience ?? generalInfo1?.experience,
                  owner2?.experience ?? generalInfo2?.experience
                )}
              />
            </tr>
            {/* A7 */}
            <tr className="compare-title">
              <td colSpan={3}>
                <h4>{t('projectProposal.general.leadingAgency.label')}</h4>
              </td>
            </tr>
            <tr>
              <td>
                <div>
                  {Array(Math.max(lead1?.length ?? 0, lead2?.length ?? 0))
                    ?.fill(null)
                    ?.map((_, index) => (
                      <div className="flex ml-5 flex-column" key={lead1?.[index]?.id ?? index}>
                        <div className="mb-2">
                          <span className="font-semibold">
                            {t('projectProposal.general.organization.name')}:{' '}
                          </span>
                          <span>
                            {
                              generateDiffStrings(
                                lead1?.[index]?.englishName === 'Other'
                                  ? lead1?.[index]?.name
                                  : t('lang') === 'vi'
                                  ? lead1?.[index]?.name
                                  : lead1?.[index]?.englishName,
                                lead2?.[index]?.englishName === 'Other'
                                  ? lead2?.[index]?.name
                                  : t('lang') === 'vi'
                                  ? lead2?.[index]?.name
                                  : lead2?.[index]?.englishName
                              )?.left
                            }
                          </span>
                        </div>
                        <div className="mb-2">
                          <span className="font-semibold">
                            {t('projectProposal.general.organization.chief')}:{' '}
                          </span>
                          <span>
                            {
                              generateDiffStrings(lead1?.[index]?.chief, lead2?.[index]?.chief)
                                ?.left
                            }
                          </span>
                        </div>
                        <div className="flex flex-wrap">
                          <div className="w-30rem mb-2">
                            <span className="font-semibold">
                              {t('projectProposal.general.organization.phone')}:{' '}
                            </span>
                            <span>
                              {
                                generateDiffStrings(lead1?.[index]?.phone, lead2?.[index]?.phone)
                                  ?.left
                              }
                            </span>
                          </div>
                          <div className="w-20rem mb-2">
                            <span className="font-semibold">
                              {t('projectProposal.general.organization.fax')}:{' '}
                            </span>
                            <span>
                              {generateDiffStrings(lead1?.[index]?.fax, lead2?.[index]?.fax)?.left}
                            </span>
                          </div>
                        </div>
                        <div className="mb-2">
                          <span className="font-semibold">
                            {t('projectProposal.general.organization.email')}:{' '}
                          </span>
                          <span>
                            {
                              generateDiffStrings(lead1?.[index]?.email, lead2?.[index]?.email)
                                ?.left
                            }
                          </span>
                        </div>
                        <div className="flex flex-wrap">
                          <div className="w-30rem mb-2">
                            <span className="font-semibold">
                              {t('projectProposal.general.organization.bankAccount')}:{' '}
                            </span>
                            <span>
                              {
                                generateDiffStrings(
                                  lead1?.[index]?.bankAccount,
                                  lead2?.[index]?.bankAccount
                                )?.left
                              }
                            </span>
                          </div>
                          <div className="mb-2">
                            <span className="font-semibold">
                              {t('projectProposal.general.organization.bank')}:{' '}
                            </span>
                            <span>
                              {
                                generateDiffStrings(lead1?.[index]?.bank, lead2?.[index]?.bank)
                                  ?.left
                              }
                            </span>
                          </div>
                        </div>
                      </div>
                    ))}
                </div>
              </td>
              <td>
                <div>
                  {Array(Math.max(lead1?.length ?? 0, lead2?.length ?? 0))
                    ?.fill(null)
                    ?.map((_, index) => (
                      <div className="flex ml-5 flex-column" key={lead2?.[index]?.id ?? index}>
                        <div className="mb-2">
                          <span className="font-semibold">
                            {t('projectProposal.general.organization.name')}:{' '}
                          </span>
                          <span>
                            {
                              generateDiffStrings(
                                lead1?.[index]?.englishName === 'Other'
                                  ? lead1?.[index]?.name
                                  : t('lang') === 'vi'
                                  ? lead1?.[index]?.name
                                  : lead1?.[index]?.englishName,
                                lead2?.[index]?.englishName === 'Other'
                                  ? lead2?.[index]?.name
                                  : t('lang') === 'vi'
                                  ? lead2?.[index]?.name
                                  : lead2?.[index]?.englishName
                              )?.right
                            }
                          </span>
                        </div>
                        <div className="mb-2">
                          <span className="font-semibold">
                            {t('projectProposal.general.organization.chief')}:{' '}
                          </span>
                          <span>
                            {
                              generateDiffStrings(lead1?.[index]?.chief, lead2?.[index]?.chief)
                                ?.right
                            }
                          </span>
                        </div>
                        <div className="flex flex-wrap">
                          <div className="w-30rem mb-2">
                            <span className="font-semibold">
                              {t('projectProposal.general.organization.phone')}:{' '}
                            </span>
                            <span>
                              {
                                generateDiffStrings(lead1?.[index]?.phone, lead2?.[index]?.phone)
                                  ?.right
                              }
                            </span>
                          </div>
                          <div className="w-20rem mb-2">
                            <span className="font-semibold">
                              {t('projectProposal.general.organization.fax')}:{' '}
                            </span>
                            <span>
                              {generateDiffStrings(lead1?.[index]?.fax, lead2?.[index]?.fax)?.right}
                            </span>
                          </div>
                        </div>
                        <div className="mb-2">
                          <span className="font-semibold">
                            {t('projectProposal.general.organization.email')}:{' '}
                          </span>
                          <span>
                            {
                              generateDiffStrings(lead1?.[index]?.email, lead2?.[index]?.email)
                                ?.right
                            }
                          </span>
                        </div>
                        <div className="flex flex-wrap">
                          <div className="w-30rem mb-2">
                            <span className="font-semibold">
                              {t('projectProposal.general.organization.bankAccount')}:{' '}
                            </span>
                            <span>
                              {
                                generateDiffStrings(
                                  lead1?.[index]?.bankAccount,
                                  lead2?.[index]?.bankAccount
                                )?.right
                              }
                            </span>
                          </div>
                          <div className="mb-2">
                            <span className="font-semibold">
                              {t('projectProposal.general.organization.bank')}:{' '}
                            </span>
                            <span>
                              {
                                generateDiffStrings(lead1?.[index]?.bank, lead2?.[index]?.bank)
                                  ?.right
                              }
                            </span>
                          </div>
                        </div>
                      </div>
                    ))}
                </div>
              </td>
              <DiffColumn
                isDifferent={
                  !!Object.values(diffLead ?? {})?.some(
                    (item) =>
                      item?.name ||
                      item?.englishName ||
                      item?.chief ||
                      item?.phone ||
                      item?.fax ||
                      item?.email ||
                      item?.bank ||
                      item?.bankAccount
                  )
                }
              />
            </tr>
            {/* A8 */}
            <tr className="compare-title">
              <td colSpan={3}>
                <h4>{t('projectProposal.general.partners.label')}</h4>
              </td>
            </tr>
            {Array(Math?.max(partners1?.length ?? 0, partners2?.length ?? 0))
              ?.fill(null)
              ?.map((_, index) => (
                <tr key={partners1?.[index]?.id ?? partners2?.[index]?.id ?? index}>
                  <td>
                    {!partners1?.[index] && index === 0 ? (
                      <span>({t('projectProposal.general.partners.noPartner')})</span>
                    ) : !partners1?.[index] && index !== 0 ? (
                      <span />
                    ) : (
                      <div
                        className={`flex flex-column ml-5 mb-2 p-1 ${highlight(
                          diffPartners?.[index] === true && !!partners1?.[index],
                          'red'
                        )}`}
                      >
                        <div className="mb-2">
                          <span className="font-bold">
                            {t('projectProposal.general.organization.name')} {index + 1}:{' '}
                          </span>
                          <span>
                            {
                              generateDiffStrings(
                                partners1?.[index]?.englishName === 'Other'
                                  ? partners1?.[index]?.name
                                  : t('lang') === 'vi'
                                  ? partners1?.[index]?.name
                                  : partners1?.[index]?.englishName,
                                partners2?.[index]?.englishName === 'Other'
                                  ? partners2?.[index]?.name
                                  : t('lang') === 'vi'
                                  ? partners2?.[index]?.name
                                  : partners2?.[index]?.englishName
                              )?.left
                            }
                          </span>
                        </div>
                        <div className="mb-2">
                          <span className="font-semibold">
                            {t('projectProposal.general.partners.confirmationDocument')}:{' '}
                          </span>
                          {partners1?.[index]?.partnerConfirmationUrl && (
                            <span
                              className={`download-compare-file pointer-events-auto p-link text-primary underline border-noround ${highlight(
                                diffPartners?.[index]?.partnerConfirmationUrl,
                                'red'
                              )}`}
                              onClick={() =>
                                handleDownloadFile(
                                  partners1?.[index]?.partnerConfirmationUrl,
                                  generalInfo1?.id
                                )
                              }
                            >
                              {t('projectProposal.general.partners.attachment')}
                            </span>
                          )}
                        </div>
                        <div className="mb-2">
                          <span className="font-semibold">
                            {t('projectProposal.general.organization.chief')}:{' '}
                          </span>
                          <span>
                            {
                              generateDiffStrings(
                                partners1?.[index]?.chief,
                                partners2?.[index]?.chief
                              )?.left
                            }
                          </span>
                        </div>
                        <div className="flex flex-wrap">
                          <div className="w-30rem mb-2">
                            <span className="font-semibold">
                              {t('projectProposal.general.organization.phone')}:{' '}
                            </span>
                            <span>
                              {
                                generateDiffStrings(
                                  partners1?.[index]?.phone,
                                  partners2?.[index]?.phone
                                )?.left
                              }
                            </span>
                          </div>
                          <div className="w-20rem mb-2">
                            <span className="font-semibold">
                              {t('projectProposal.general.organization.fax')}:{' '}
                            </span>
                            <span>
                              {
                                generateDiffStrings(
                                  partners1?.[index]?.fax,
                                  partners2?.[index]?.fax
                                )?.left
                              }
                            </span>
                          </div>
                        </div>
                        <div className="mb-2">
                          <span className="font-semibold">
                            {t('projectProposal.general.organization.address')}:{' '}
                          </span>
                          <span>
                            {
                              generateDiffStrings(
                                partners1?.[index]?.address,
                                partners2?.[index]?.address
                              )?.left
                            }
                          </span>
                        </div>
                        <div
                          className={
                            index === generalInfo1.projectProposalOrganizations.length - 2
                              ? ''
                              : 'mb-3'
                          }
                        >
                          <div>
                            <span className="font-semibold">
                              {t('projectProposal.general.organization.partnerContribution')}:{' '}
                            </span>
                          </div>
                          <p>
                            {
                              generateDiffStrings(
                                partners1?.[index]?.partnerContribution,
                                partners2?.[index]?.partnerContribution
                              )?.left
                            }
                          </p>
                        </div>
                      </div>
                    )}
                  </td>
                  <td>
                    {!partners2?.[index] && index === 0 ? (
                      <span>({t('projectProposal.general.partners.noPartner')})</span>
                    ) : !partners2?.[index] && index !== 0 ? (
                      <span> </span>
                    ) : (
                      <div
                        className={`flex flex-column ml-5 mb-2 p-1 ${highlight(
                          diffPartners?.[index] === true && !!partners2?.[index]
                        )}`}
                      >
                        <div className="mb-2">
                          <span className="font-bold">
                            {t('projectProposal.general.organization.name')} {index + 1}:{' '}
                          </span>
                          <span>
                            {
                              generateDiffStrings(
                                partners1?.[index]?.englishName === 'Other'
                                  ? partners1?.[index]?.name
                                  : t('lang') === 'vi'
                                  ? partners1?.[index]?.name
                                  : partners1?.[index]?.englishName,
                                partners2?.[index]?.englishName === 'Other'
                                  ? partners2?.[index]?.name
                                  : t('lang') === 'vi'
                                  ? partners2?.[index]?.name
                                  : partners2?.[index]?.englishName
                              )?.right
                            }
                          </span>
                        </div>
                        <div className="mb-2">
                          <span className="font-semibold">
                            {t('projectProposal.general.partners.confirmationDocument')}:{' '}
                          </span>
                          {partners2?.[index]?.partnerConfirmationUrl && (
                            <span
                              className={`download-compare-file pointer-events-auto p-link text-primary underline border-noround ${highlight(
                                diffPartners?.[index]?.partnerConfirmationUrl,
                                'green'
                              )}`}
                              onClick={() =>
                                handleDownloadFile(
                                  partners2?.[index]?.partnerConfirmationUrl,
                                  generalInfo1?.id
                                )
                              }
                            >
                              {t('projectProposal.general.partners.attachment')}
                            </span>
                          )}
                        </div>
                        <div className="mb-2">
                          <span className="font-semibold">
                            {t('projectProposal.general.organization.chief')}:{' '}
                          </span>
                          <span>
                            {
                              generateDiffStrings(
                                partners1?.[index]?.chief,
                                partners2?.[index]?.chief
                              )?.right
                            }
                          </span>
                        </div>
                        <div className="flex flex-wrap">
                          <div className="w-30rem mb-2">
                            <span className="font-semibold">
                              {t('projectProposal.general.organization.phone')}:{' '}
                            </span>
                            <span>
                              {
                                generateDiffStrings(
                                  partners1?.[index]?.phone,
                                  partners2?.[index]?.phone
                                )?.right
                              }
                            </span>
                          </div>
                          <div className="w-20rem mb-2">
                            <span className="font-semibold">
                              {t('projectProposal.general.organization.fax')}:{' '}
                            </span>
                            <span>
                              {
                                generateDiffStrings(
                                  partners1?.[index]?.fax,
                                  partners2?.[index]?.fax
                                )?.right
                              }
                            </span>
                          </div>
                        </div>
                        <div className="mb-2">
                          <span className="font-semibold">
                            {t('projectProposal.general.organization.address')}:{' '}
                          </span>
                          <span>
                            {
                              generateDiffStrings(
                                partners1?.[index]?.address,
                                partners2?.[index]?.address
                              )?.right
                            }
                          </span>
                        </div>
                        <div
                          className={
                            index === generalInfo2.projectProposalOrganizations.length - 2
                              ? ''
                              : 'mb-3'
                          }
                        >
                          <div>
                            <span className="font-semibold">
                              {t('projectProposal.general.organization.partnerContribution')}:{' '}
                            </span>
                          </div>
                          <p>
                            {
                              generateDiffStrings(
                                partners1?.[index]?.partnerContribution,
                                partners2?.[index]?.partnerContribution
                              )?.right
                            }
                          </p>
                        </div>
                      </div>
                    )}
                  </td>
                  <DiffColumn
                    isDifferent={
                      diffPartners?.[index] === true ||
                      Object.entries(diffPartners?.[index] ?? {})?.some(
                        ([key, value]) =>
                          (key === 'name' ||
                            key === 'englishName' ||
                            key === 'chief' ||
                            key === 'phone' ||
                            key === 'fax' ||
                            key === 'email' ||
                            key === 'bank' ||
                            key === 'bankAccount' ||
                            key === 'address' ||
                            key === 'partnerContribution' ||
                            key === 'partnerConfirmationUrl') &&
                          value
                      )
                    }
                  />
                </tr>
              ))}
            {/* A9 */}
            <tr className="compare-title">
              <td colSpan={3}>
                <h4>{t('projectProposal.general.researchHumanResources.label')}</h4>
              </td>
            </tr>
            {/* Owner */}
            <tr className="compare-title">
              <td colSpan={3}>
                <h4>{t('projectProposal.general.researchHumanResources.ownerFields.label')}</h4>
                <p>{t('projectProposal.general.researchHumanResources.ownerFields.note')}</p>
              </td>
            </tr>

            <ContactCompareTable
              data1={[owner1]}
              data2={[owner2]}
              diffObj={diff(
                [
                  {
                    academicRank: owner1?.academicRank,
                    academicDegree: owner1?.academicDegree,
                    fullname: owner1?.fullname,
                    organizationName:
                      owner1?.organization?.englishName === 'Other'
                        ? owner1?.specificOrganizationName
                        : t('lang') === 'vi'
                        ? owner1?.organization?.name
                        : owner1?.organization?.englishName,
                  },
                ],
                [
                  {
                    academicRank: owner2?.academicRank,
                    academicDegree: owner2?.academicDegree,
                    fullname: owner2?.fullname,
                    organizationName:
                      owner2?.organization?.englishName === 'Other'
                        ? owner2?.specificOrganizationName
                        : t('lang') === 'vi'
                        ? owner2?.organization?.name
                        : owner2?.organization?.englishName,
                  },
                ]
              )}
              isLoading={false}
            />
            {/* Scientific secretary */}
            <tr className="compare-title">
              <td colSpan={3}>
                <h4>
                  {t(
                    'projectProposal.general.researchHumanResources.scientificSecretaryFields.label'
                  )}
                </h4>
              </td>
            </tr>
            <ContactCompareTable
              data1={scientificSecretaries1}
              data2={scientificSecretaries2}
              diffObj={diffScientificSecretary}
              isLoading={false}
            />
            {/* Main participants */}
            <tr className="compare-title">
              <td colSpan={3}>
                <h4>
                  {t('projectProposal.general.researchHumanResources.mainParticipantFields.label')}
                </h4>
                <p>{t('projectProposal.general.researchHumanResources.ownerFields.note')}</p>
              </td>
            </tr>
            <ContactCompareTable
              data1={mainParticipants1}
              data2={mainParticipants2}
              diffObj={diffMainParticipant}
              isLoading={false}
            />
            {/* Participants */}
            <tr className="compare-title">
              <td colSpan={3}>
                <h4>
                  {t('projectProposal.general.researchHumanResources.participantFields.label')}
                </h4>
              </td>
            </tr>
            <ContactCompareTable
              data1={participants1}
              data2={participants2}
              diffObj={diffParticipant}
              isLoading={false}
            />
            {/* Technicians */}
            <tr className="compare-title">
              <td colSpan={3}>
                <h4>
                  {t('projectProposal.general.researchHumanResources.technicianFields.label')}
                </h4>
              </td>
            </tr>
            <ContactCompareTable
              data1={technicians1}
              data2={technicians2}
              diffObj={diffTechnician}
              isLoading={false}
            />
            {/* DomesticExperts */}
            <tr className="compare-title">
              <td colSpan={3}>
                <h4>
                  {t('projectProposal.general.researchHumanResources.domesticExpertFields.label')}
                </h4>
              </td>
            </tr>
            <ContactCompareTable
              data1={domesticExperts1}
              data2={domesticExperts2}
              diffObj={diffDomesticExpert}
              isLoading={false}
            />
            {/* InternationalExperts */}
            <tr className="compare-title">
              <td colSpan={3}>
                <h4>
                  {t(
                    'projectProposal.general.researchHumanResources.internationalExpertFields.label'
                  )}
                </h4>
              </td>
            </tr>
            <ContactCompareTable
              data1={internationalExperts1}
              data2={internationalExperts2}
              diffObj={diffInternationalExpert}
              isLoading={false}
            />
            <tr className="compare-title">
              <td colSpan={3}>
                <h4>{t('projectProposal.researchResult.viewLabel')}</h4>
                <h4>{t('projectProposal.researchResult.productAndResearchResultDes.label')}</h4>
              </td>
            </tr>
            {/* Soft products */}
            <tr className="compare-title">
              <td colSpan={3}>
                <h5 className="font-semibold">
                  {t(
                    'projectProposal.researchResult.productAndResearchResultDes.softProduct.label'
                  )}
                </h5>
                <p className="font-italic">
                  {t('projectProposal.researchResult.productAndResearchResultDes.softProduct.note')}
                </p>
              </td>
            </tr>
            <SoftProductCompareTable
              products1={researchResult1?.softProducts}
              products2={researchResult2?.softProducts}
              diffObj={diffSoftProduct}
            />
            {/* Hard products */}
            <tr className="compare-title">
              <td colSpan={3}>
                <h5 className="font-semibold">
                  {t(
                    'projectProposal.researchResult.productAndResearchResultDes.hardProduct.label'
                  )}
                </h5>
                <p className="font-italic">
                  {t('projectProposal.researchResult.productAndResearchResultDes.hardProduct.note')}
                </p>
              </td>
            </tr>
            <HardProductCompareTable
              products1={researchResult1?.hardProducts}
              products2={researchResult2?.hardProducts}
              diffObj={diffHardProduct}
              qualityComparison={researchResult1?.qualityComparison}
            />
            <tr className="compare-title">
              <td colSpan={3}>
                <h5 className="font-semibold mt-2">
                  {t(
                    'projectProposal.researchResult.productAndResearchResultDes.qualityComparison.label'
                  )}
                </h5>
                <p className="font-italic">
                  (
                  {t(
                    'projectProposal.researchResult.productAndResearchResultDes.qualityComparison.note'
                  )}
                  )
                </p>
              </td>
            </tr>
            <tr>
              <td>
                <div className="mt-3 mb-1">
                  {
                    generateDiffStrings(
                      researchResult1?.qualityComparison,
                      researchResult2?.qualityComparison
                    )?.left
                  }
                </div>
              </td>
              <td>
                <div className="mt-3 mb-1">
                  {
                    generateDiffStrings(
                      researchResult1?.qualityComparison,
                      researchResult2?.qualityComparison
                    )?.right
                  }
                </div>
              </td>
              <DiffColumn
                isDifferent={
                  researchResult1?.qualityComparison !== researchResult2?.qualityComparison
                }
                content={renderDiffString(
                  researchResult1?.qualityComparison,
                  researchResult2?.qualityComparison
                )}
              />
            </tr>
            {/* Research Kpis */}
            <tr className="compare-title">
              <td colSpan={3}>
                <h4>{t('projectProposal.researchResult.kpis.label')}</h4>
              </td>
            </tr>
            <KPICompareTable
              researchKpis1={researchResult1?.projectProposalResearchKpis}
              researchKpis2={researchResult2?.projectProposalResearchKpis}
              diffObj={diffResearchKpi}
            />

            {/* A11 */}
            {/* A11.1 */}
            <tr className="compare-title">
              <td colSpan={3}>
                <h4>{t('projectProposal.researchResult.researchContributions.label')}</h4>
                <h4 className="mb-2 ">
                  {t(
                    'projectProposal.researchResult.researchContributions.contributionAndResolution'
                  )}
                </h4>
                <p className="font-italic">
                  {t(
                    'projectProposal.researchResult.researchContributions.contributionAndResolutionNote'
                  )}
                </p>
              </td>
            </tr>
            <tr>
              <td>
                {researchResult1?.knowledgeContributionLevel && (
                  <RichTextViewer
                    value={researchResult1?.knowledgeContributionLevel ?? ''}
                    showHeader={false}
                    readOnly
                  />
                )}
              </td>
              <td>
                {researchResult2?.knowledgeContributionLevel && (
                  <RichTextViewer
                    value={researchResult2?.knowledgeContributionLevel ?? ''}
                    showHeader={false}
                    readOnly
                  />
                )}
              </td>
              <DiffColumn isDifferent={diffA11?.knowledgeContributionLevel} />
            </tr>
            {/* A11.2 */}
            <tr className="compare-title">
              <td colSpan={3}>
                <h4 className="mb-2 ">
                  {t('projectProposal.researchResult.researchContributions.practicalImpact')}
                </h4>
                <p className="font-italic">
                  {t('projectProposal.researchResult.researchContributions.practicalImpactNote')}
                </p>
              </td>
            </tr>
            <tr>
              <td>
                {researchResult1?.practicalPolicyImpact && (
                  <RichTextViewer
                    value={researchResult1?.practicalPolicyImpact ?? ''}
                    showHeader={false}
                    readOnly
                  />
                )}
              </td>
              <td>
                {researchResult2?.practicalPolicyImpact && (
                  <RichTextViewer
                    value={researchResult2?.practicalPolicyImpact ?? ''}
                    showHeader={false}
                    readOnly
                  />
                )}
              </td>
              <DiffColumn isDifferent={diffA11?.practicalPolicyImpact} />
            </tr>
            {/* A11.3 */}
            <tr className="compare-title">
              <td colSpan={3}>
                <h4 className="mb-2 ">
                  {t(
                    'projectProposal.researchResult.researchContributions.researchGroupDevelopment.label'
                  )}
                </h4>
                <p className="font-italic">
                  {t(
                    'projectProposal.researchResult.researchContributions.researchGroupDevelopment.note'
                  )}
                </p>
              </td>
            </tr>
            <tr>
              <td>
                {researchResult1?.teamDevelopmentLevel && (
                  <RichTextViewer
                    value={researchResult1?.teamDevelopmentLevel ?? ''}
                    showHeader={false}
                    readOnly
                  />
                )}
              </td>
              <td>
                {researchResult2?.teamDevelopmentLevel && (
                  <RichTextViewer
                    value={researchResult2?.teamDevelopmentLevel ?? ''}
                    showHeader={false}
                    readOnly
                  />
                )}
              </td>
              <DiffColumn isDifferent={diffA11?.teamDevelopmentLevel} />
            </tr>
            {/* A11.4 */}
            <tr className="compare-title">
              <td colSpan={3}>
                <h4 className="mb-2 ">
                  {t(
                    'projectProposal.researchResult.researchContributions.abilityTransferResearchResults.label'
                  )}
                </h4>
                <p className="font-italic">
                  {t(
                    'projectProposal.researchResult.researchContributions.abilityTransferResearchResults.note'
                  )}
                </p>
              </td>
            </tr>
            <tr>
              <td>
                {generalInfo1?.researchType?.code === 'implementation' &&
                  researchResult1?.researchTransferability && (
                    <RichTextViewer
                      value={researchResult1?.researchTransferability ?? ''}
                      showHeader={false}
                      readOnly
                    />
                  )}
              </td>
              <td>
                {generalInfo2?.researchType?.code === 'implementation' &&
                  researchResult2?.researchTransferability && (
                    <RichTextViewer
                      value={researchResult2?.researchTransferability ?? ''}
                      showHeader={false}
                      readOnly
                    />
                  )}
              </td>
              <DiffColumn
                isDifferent={
                  (generalInfo1?.researchType?.code === 'implementation' ||
                    generalInfo2?.researchType?.code === 'implementation') &&
                  diffA11?.researchTransferability
                }
              />
            </tr>

            {/* B */}
            <tr className="compare-title">
              <td colSpan={3}>
                <h4 className="pt-3">{t('projectProposal.researchDescription.viewLabel')}</h4>
              </td>
            </tr>
            <tr>
              <td className="p-0">
                <div className="mb-2">
                  <h4>
                    B1 - B5.{' '}
                    {documents1?.researchDescriptionFileUrl && (
                      <span
                        className={`download-compare-file pointer-events-auto p-link text-primary underline border-noround font-normal ${highlight(
                          diffDocument?.researchDescriptionFileUrl,
                          'red'
                        )}`}
                        onClick={() =>
                          handleDownloadFile(
                            documents1?.researchDescriptionFileUrl,
                            generalInfo1?.id
                          )
                        }
                      >
                        {t('projectProposal.researchDescription.attachment')}
                      </span>
                    )}
                  </h4>
                </div>
              </td>
              <td className="p-0">
                <div className="mb-2">
                  <h4>
                    B1 - B5.{' '}
                    {documents2?.researchDescriptionFileUrl && (
                      <span
                        className={`download-compare-file pointer-events-auto p-link text-primary underline border-noround font-normal ${highlight(
                          diffDocument?.researchDescriptionFileUrl
                        )}`}
                        onClick={() =>
                          handleDownloadFile(
                            documents2?.researchDescriptionFileUrl,
                            generalInfo2?.id
                          )
                        }
                      >
                        {t('projectProposal.researchDescription.attachment')}
                      </span>
                    )}
                  </h4>
                </div>
              </td>
              <DiffColumn isDifferent={diffDocument?.researchDescriptionFileUrl} />
            </tr>

            {/* Documents */}
            <tr className="compare-title">
              <td colSpan={3}>
                <h4 className="pt-3">{t('projectProposal.documents.label')}</h4>
              </td>
            </tr>
            <tr className="datatable">
              <td>
                {/* eslint-disable indent */}
                {!documents1.expenditureFileUrl &&
                !documents1?.researchDescriptionProofFiles?.length ? (
                  <span>({t('projectProposal.documents.noAttachment')})</span>
                ) : (
                  <ul className="list-disc text-lg">
                    {documents1?.expenditureFileUrl ? (
                      <li className="mb-2">
                        <span
                          className={`download-compare-file pointer-events-auto p-link text-primary underline border-noround ${highlight(
                            diffDocument?.expenditureFileUrl,
                            'red'
                          )}`}
                          onClick={() =>
                            handleDownloadFile(documents1?.expenditureFileUrl, generalInfo1?.id)
                          }
                        >
                          {t('projectProposal.documents.explanatoryAppendix')}
                        </span>
                      </li>
                    ) : (
                      <li className="mb-2 list-none"> </li>
                    )}
                    {documents1?.researchDescriptionProofFiles?.length > 0 ? (
                      <li
                        className={`mb-2 ${highlight(
                          documents1?.researchDescriptionProofFiles?.length > 0 &&
                            !(documents2?.researchDescriptionProofFiles?.length > 0),
                          'red'
                        )}`}
                      >
                        <span className="p-link" style={{ cursor: 'default', color: '#495057' }}>
                          {t('projectProposal.documents.otherProofFilesAndAppendices')}:
                        </span>
                        <ul
                          className={`-ml-4 mt-1 ${highlight(
                            Object.values(diffDocument?.researchDescriptionProofFiles ?? {})?.some(
                              (item) => item === true
                            ),
                            'red'
                          )}`}
                        >
                          {documents1?.researchDescriptionProofFiles?.map((item, index) => {
                            if (item?.fileName) {
                              return (
                                <li key={item.id ?? index} className="pl-0 ml-0 mt-1">
                                  <span
                                    className="download-compare-file pointer-events-auto p-link text-primary underline border-noround"
                                    onClick={() =>
                                      handleDownloadFile(item?.fileName, generalInfo1?.id)
                                    }
                                  >
                                    {formatFileName(item?.fileName)}
                                  </span>
                                </li>
                              );
                            }

                            return null;
                          })}
                        </ul>
                      </li>
                    ) : (
                      <li className="mb-2 list-none"> </li>
                    )}
                  </ul>
                )}
              </td>
              <td>
                {/* eslint-disable indent */}
                {!documents2.expenditureFileUrl &&
                !documents2?.researchDescriptionProofFiles?.length ? (
                  <span>({t('projectProposal.documents.noAttachment')})</span>
                ) : (
                  <ul className="list-disc text-lg">
                    {documents2?.expenditureFileUrl && (
                      <li className="mb-2">
                        <span
                          className={`download-compare-file pointer-events-auto p-link text-primary underline border-noround ${highlight(
                            diffDocument?.expenditureFileUrl
                          )}`}
                          onClick={() =>
                            handleDownloadFile(documents2?.expenditureFileUrl, generalInfo2?.id)
                          }
                        >
                          {t('projectProposal.documents.explanatoryAppendix')}
                        </span>
                      </li>
                    )}
                    {documents2?.researchDescriptionProofFiles?.length > 0 && (
                      <li
                        className={`mb-2 ${highlight(
                          documents2?.researchDescriptionProofFiles?.length > 0 &&
                            !(documents1?.researchDescriptionProofFiles?.length > 0)
                        )}`}
                      >
                        <span className="p-link" style={{ cursor: 'default', color: '#495057' }}>
                          {t('projectProposal.documents.otherProofFilesAndAppendices')}:
                        </span>
                        <ul
                          className={`-ml-4 mt-1 ${highlight(
                            Object.values(diffDocument?.researchDescriptionProofFiles ?? {})?.some(
                              (item) => item === true
                            )
                          )}`}
                        >
                          {documents2?.researchDescriptionProofFiles?.map((item, index) => {
                            if (item?.fileName) {
                              return (
                                <li key={item.id ?? index} className="pl-0 ml-0 mt-1">
                                  <span
                                    className={`download-compare-file pointer-events-auto p-link text-primary underline border-noround ${highlight(
                                      diffDocument?.fileName
                                    )}`}
                                    onClick={() =>
                                      handleDownloadFile(item?.fileName, generalInfo2?.id)
                                    }
                                  >
                                    {formatFileName(item?.fileName)}
                                  </span>
                                </li>
                              );
                            }

                            return null;
                          })}
                        </ul>
                      </li>
                    )}
                  </ul>
                )}
              </td>
              <DiffColumn
                isDifferent={Object.values(diffDocument ?? {} ?? {}).some(
                  (e) =>
                    e === true ||
                    Object.values(e ?? {})?.some((p) => p?.evaluativeCriteria || p?.name || p?.note)
                )}
              />
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  );
}
